import { useState } from "react";
import { Sparkles, FileSignature, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useDeals } from "@/hooks/useDeals";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

interface GenerateContractDialogProps {
  preselectedDealId?: string;
  trigger?: React.ReactNode;
}

export function GenerateContractDialog({ preselectedDealId, trigger }: GenerateContractDialogProps) {
  const [open, setOpen] = useState(false);
  const [selectedDealId, setSelectedDealId] = useState(preselectedDealId || "");
  const [isGenerating, setIsGenerating] = useState(false);
  const { data: deals } = useDeals();
  const queryClient = useQueryClient();

  // Filter deals that don't already have contracts or are open
  const availableDeals = deals?.filter(deal => deal.status === 'open') || [];

  const handleGenerate = async () => {
    if (!selectedDealId) {
      toast.error("Please select a deal");
      return;
    }

    setIsGenerating(true);

    try {
      const { data, error } = await supabase.functions.invoke('generate-contract', {
        body: { dealId: selectedDealId },
      });

      if (error) throw error;

      if (data.success) {
        toast.success("Contract generated successfully!", {
          description: data.contract.title,
        });
        queryClient.invalidateQueries({ queryKey: ['contracts'] });
        setOpen(false);
        setSelectedDealId("");
      } else {
        throw new Error(data.error || 'Failed to generate contract');
      }
    } catch (error: any) {
      console.error('Contract generation error:', error);
      toast.error("Failed to generate contract", {
        description: error.message,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const selectedDeal = deals?.find(d => d.id === selectedDealId);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="gap-2 ai-glow">
            <Sparkles className="h-4 w-4" />
            AI Generate Contract
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-primary" />
            Generate Contract with AI
          </DialogTitle>
          <DialogDescription>
            Select a deal to automatically generate a professional contract using AI. 
            The contract will include deal details, line items, and standard terms.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="deal">Select Deal</Label>
            <Select value={selectedDealId} onValueChange={setSelectedDealId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a deal..." />
              </SelectTrigger>
              <SelectContent>
                {availableDeals.map((deal) => (
                  <SelectItem key={deal.id} value={deal.id}>
                    <div className="flex items-center justify-between gap-4">
                      <span>{deal.title}</span>
                      <span className="text-muted-foreground text-sm">
                        ${deal.amount?.toLocaleString() || 0}
                      </span>
                    </div>
                  </SelectItem>
                ))}
                {availableDeals.length === 0 && (
                  <SelectItem value="none" disabled>
                    No open deals available
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {selectedDeal && (
            <div className="rounded-lg border border-border p-4 space-y-2 bg-muted/30">
              <h4 className="font-medium">{selectedDeal.title}</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Amount: </span>
                  <span className="font-medium">
                    ${selectedDeal.amount?.toLocaleString() || 0} {selectedDeal.currency}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Stage: </span>
                  <span className="font-medium">
                    {selectedDeal.pipeline_stages?.name || 'N/A'}
                  </span>
                </div>
                {selectedDeal.accounts && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Account: </span>
                    <span className="font-medium">{selectedDeal.accounts.name}</span>
                  </div>
                )}
                {selectedDeal.expected_close_date && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Expected Close: </span>
                    <span className="font-medium">
                      {new Date(selectedDeal.expected_close_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="text-sm text-muted-foreground bg-primary/5 rounded-lg p-3">
            <p className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              AI will generate:
            </p>
            <ul className="mt-2 ml-6 list-disc space-y-1">
              <li>Executive summary</li>
              <li>Scope of work</li>
              <li>Pricing and payment terms</li>
              <li>Terms and conditions</li>
              <li>Timeline and milestones</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleGenerate} 
            disabled={!selectedDealId || isGenerating}
            className="gap-2"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Generate Contract
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
